import { FC, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Drawer, Layout, Menu } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import useResponsive from "../../../hooks/useResponsive";
import { Logo } from "../../../assets/images";
import { PageRoutesConstant } from "../router/components/PageRoutes";
import { ServicesEnum } from "../../enums/Services";
import "./MainHeader.style.scss";

const { Header } = Layout;

const MenuConstant = [
  {
    key: PageRoutesConstant.Home.path,
    label: "Home",
  },
  {
    key: PageRoutesConstant.About.path,
    label: "About Us",
  },
  {
    key: "services",
    label: "Services",
    popupOffset: [-8, -8],
    children: [
      {
        key: PageRoutesConstant.WebDevelopment.path,
        label: ServicesEnum.WEB_DEVELOPMENT,
      },
      {
        key: PageRoutesConstant.ProductDevelopment.path,
        label: ServicesEnum.PRODUCT_DEVELOPMENT,
      },
      {
        key: PageRoutesConstant.UiUxDesign.path,
        label: ServicesEnum.UI_UX_DEVELOPMENT,
      },
    ],
  },
  {
    key: PageRoutesConstant.Portfolio.path,
    label: "Portfolio",
  },
  {
    key: PageRoutesConstant.Career.path,
    label: "Career",
  },
  {
    key: PageRoutesConstant.Contact.path,
    label: "Contact Us",
  },
];

const MainHeader: FC = () => {
  const { isMobile, isTablet } = useResponsive();

  return (
    <Header className="header">
      <div className="container-md">
        <div className="header_menu">
          <Link
            to={PageRoutesConstant.Home.path}
            className="header_logo"
            aria-label="BIGELEMENT INFOTECH - Home"
          >
            <img
              src={Logo}
              alt="BIGELEMENT INFOTECH - Software Development Company"
              className="header_logo_image img-fluid"
              width={140}
              height={50}
              loading="eager"
              decoding="async"
            />
          </Link>
          <MenuList />
          {!isMobile && !isTablet && (
            <Link
              to={PageRoutesConstant.WorkWithYou.path}
              className="header_hire_btn ms-3"
            >
              Hire Us
            </Link>
          )}
        </div>
      </div>
    </Header>
  );
};

const MenuList: FC = () => {
  const [visible, setVisible] = useState(false);
  const { isMobile, isTablet } = useResponsive();

  return (
    <>
      {isMobile || isTablet ? (
        <div className="d-flex align-items-center">
          <Button
            onClick={() => setVisible(true)}
            className="header_drawer_button"
            aria-label="Open navigation menu"
          >
            <MenuOutlined />
          </Button>
          <Drawer
            placement="right"
            onClose={() => setVisible(false)}
            open={visible}
          >
            <MenuBar items={MenuConstant} setVisible={setVisible} />
            <Link
              to={PageRoutesConstant.WorkWithYou.path}
              className="header_hire_btn ms-3 mt-3"
              onClick={() => setVisible(false)}
            >
              Hire Us
            </Link>
          </Drawer>
        </div>
      ) : (
        <div className="header_menu_content">
          <MenuBar items={MenuConstant} />
        </div>
      )}
    </>
  );
};

type MenuProps = {
  items: {
    key: string;
    label: string;
    children?: { key: string; label: string }[];
  }[];
  setVisible?: (value: boolean) => void;
};

const MenuBar: FC<MenuProps> = ({ items, setVisible }) => {
  const { isMobile, isTablet } = useResponsive();
  const location = useLocation();
  const [selectedMenu, setSelectedMenu] = useState(
    PageRoutesConstant.Home.path,
  );

  useEffect(() => {
    if (location.pathname.includes("career")) {
      setSelectedMenu(PageRoutesConstant.Career.path);
    } else {
      setSelectedMenu(location.pathname);
    }
  }, [location]);

  const menuItems = useMemo(
    () =>
      items.map((item) => {
        if (item.key === "services") {
          return {
            ...item,
            label: "Services",
            children: item.children?.map((ch) => ({
              key: ch.key,
              label: (
                <Link
                  to={ch.key}
                  onClick={() => setVisible?.(false)}
                  className="header_menu_link"
                >
                  {ch.label}
                </Link>
              ),
            })),
          };
        }
        return {
          ...item,
          label: (
            <Link
              to={item.key}
              onClick={() => setVisible?.(false)}
              className="header_menu_link"
            >
              {item.label}
            </Link>
          ),
        };
      }),
    [items, setVisible],
  );

  return (
    <Menu
      mode={isMobile || isTablet ? "inline" : "horizontal"}
      defaultSelectedKeys={[selectedMenu]}
      selectedKeys={[selectedMenu]}
      items={menuItems}
      onClick={() => setVisible?.(false)}
      style={{ flex: "auto", minWidth: 0 }}
    />
  );
};

export default MainHeader;
